import { type CSSProperties, useEffect, useState } from "react";
import { X } from "lucide-react";
import { useReveal } from "../hooks/useReveal";
import { Link } from "react-router-dom";
import emporiaImg from "../new-assets/emporia-ai2.png";
import commercialExpandedImg from "../new-assets/comercial pic-06-25.png";

type Service = {
  title: string;
  description: string;
  image: string;
  expandedImage?: string;
  expanded?: {
    intro: string;
    sections: {
      title: string;
      items: string[];
    }[];
    note: string;
  };
};

type CardOrigin = {
  top: number;
  left: number;
  width: number;
  height: number;
};

type SelectedService = {
  service: Service;
  origin: CardOrigin;
};

const services: Service[] = [
  {
    title: "Commercial Services",
    description: "Power One Electric delivers commercial electrical solutions from planning through final inspection.",
    image: commercialExpandedImg,
    expandedImage: commercialExpandedImg,
    expanded: {
      intro: "Power One Electric delivers commercial electrical solutions from planning through final inspection. Whether it’s a tenant build-out, renovation, service upgrade, or ground-up project, we provide organized, code-compliant installations that keep projects moving and businesses operating safely.",
      sections: [
        {
          title: "Why General Contractors Work With Us",
          items: [
            "Clear and organized proposals",
            "Experience reading plans, specs, and addenda",
            "Coordination with project managers, supers, and other trades",
            "Fast response on RFIs, changes, and field issues",
            "Clean installations that help avoid rework",
            "Reliable manpower planning for active job sites",
            "Code-compliant work ready for inspection",
          ],
        },
        {
          title: "What We Bring to the Job",
          items: [
            "Power distribution",
            "Lighting systems",
            "Panel and service upgrades",
            "Dedicated equipment circuits",
            "Low-voltage rough-in support",
            "Fire alarm coordination",
            "Emergency and exit lighting",
            "Troubleshooting and punch-list completion",
          ],
        },
        {
          title: "Reliable Project Execution",
          items: [
            "Accurate takeoffs and estimating before the job starts",
            "Clear communication from bid to closeout",
            "Material planning to reduce delays",
            "Field coordination with other trades",
            "Change order support when scope changes",
            "Final walkthrough and inspection support",
          ],
        },
      ],
      note: "Built for general contractors, property managers, developers, and business owners who need dependable electrical execution without rework, confusion, or delays.",
    },
  },
  {
    title: "Energy Efficiency and Safety",
    description: "Smart meters, LED conversions, EV charger installation, and energy tracking to cut your bills and reduce your footprint. Featuring VUE Smart Systems.",
    image: emporiaImg,
  },
  {
    title: "Residential Electrical",
    description: "Power One Electric is a residential electrical contractor serving homeowners across Louisiana.",
    image: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?q=80&w=900&auto=format&fit=crop",
    expanded: {
      intro: "Power One Electric is a residential electrical contractor serving homeowners across Louisiana. We handle new construction, remodeling, service upgrades, and installations like standby generators and EV chargers, delivering code-compliant, reliable systems with expert craftsmanship and attention to detail.",
      sections: [
        {
          title: "Why Homeowners Choose Us",
          items: [
            "Honest, upfront pricing",
            "Licensed and code-compliant work",
            "Friendly, respectful electricians",
            "Clean workmanship and job sites",
            "Reliable scheduling and communication",
            "Quality materials from trusted brands",
            "Solutions built to last",
          ],
        },
        {
          title: "Residential Services We Provide",
          items: [
            "Whole-home wiring",
            "Electrical panel upgrades and replacements",
            "Home standby generators",
            "EV charger installation",
            "Indoor and outdoor lighting",
            "Ceiling fans, outlets, and switches",
            "Smart home device installation",
            "Whole-home surge protection",
            "Electrical troubleshooting and repairs",
            "Home remodels and additions",
            "Pool and patio electrical",
            "Safety inspections and code corrections",
          ],
        },
        {
          title: "Your Home. Your Safety.",
          items: [
            "Protect your family with safe, code-compliant electrical work",
            "Prevent future electrical problems before they become costly repairs",
            "Improve comfort, convenience, and energy efficiency",
            "Increase your home’s value with professional electrical upgrades",
            "Gain peace of mind knowing the work is done right the first time",
          ],
        },
      ],
      note: "Best fit for homeowners who need reliable repairs, safer panels, better power capacity, or modern electrical upgrades.",
    },
  },
];

export function Services() {
  const headerRef = useReveal<HTMLDivElement>();
  const [selectedService, setSelectedService] = useState<SelectedService | null>(null);
  const [isClosing, setIsClosing] = useState(false);

  function openService(service: Service, origin: DOMRect) {
    if (isClosing) return;

    setSelectedService({
      service,
      origin: {
        top: origin.top,
        left: origin.left,
        width: origin.width,
        height: origin.height,
      },
    });
    setIsClosing(false);
  }

  function closeService() {
    if (!selectedService || isClosing) return;

    setIsClosing(true);
    window.setTimeout(() => {
      setSelectedService(null);
      setIsClosing(false);
    }, 760);
  }

  useEffect(() => {
    if (!selectedService) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        closeService();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedService, isClosing]);

  return (
    <section id="services" className="py-24 relative overflow-hidden bg-[#EEF2FF]">
      <style>{`
        @keyframes arc-travel-1 {
          0%   { stroke-dashoffset: 1200; opacity: 0; }
          3%   { opacity: 1; }
          55%  { stroke-dashoffset: 0; opacity: 1; }
          65%  { opacity: 0.4; }
          70%  { stroke-dashoffset: 0; opacity: 0; }
          100% { stroke-dashoffset: 0; opacity: 0; }
        }
        @keyframes arc-travel-2 {
          0%, 25%  { stroke-dashoffset: 1200; opacity: 0; }
          28%  { opacity: 1; }
          75%  { stroke-dashoffset: 0; opacity: 1; }
          83%  { opacity: 0; }
          100% { stroke-dashoffset: 0; opacity: 0; }
        }
        @keyframes arc-travel-3 {
          0%, 45%  { stroke-dashoffset: 1200; opacity: 0; }
          48%  { opacity: 1; }
          88%  { stroke-dashoffset: 0; opacity: 1; }
          95%  { opacity: 0; }
          100% { stroke-dashoffset: 0; opacity: 0; }
        }
        @keyframes arc-travel-4 {
          0%, 60%  { stroke-dashoffset: 1200; opacity: 0; }
          62%  { opacity: 1; }
          95%  { stroke-dashoffset: 0; opacity: 1; }
          99%  { opacity: 0; }
          100% { stroke-dashoffset: 0; opacity: 0; }
        }
        @keyframes arc-travel-5 {
          0%, 15%  { stroke-dashoffset: 1200; opacity: 0; }
          17%  { opacity: 1; }
          60%  { stroke-dashoffset: 0; opacity: 1; }
          68%  { opacity: 0; }
          100% { stroke-dashoffset: 0; opacity: 0; }
        }
        @keyframes wall-spark-left {
          0%, 100% { opacity: 0; transform: scale(0.5); }
          5%  { opacity: 1; transform: scale(1.4); }
          15% { opacity: 0; transform: scale(0.8); }
          28% { opacity: 1; transform: scale(1.2); }
          35% { opacity: 0; }
          63% { opacity: 1; transform: scale(1.5); }
          70% { opacity: 0; }
        }
        @keyframes wall-spark-right {
          0%, 100% { opacity: 0; transform: scale(0.5); }
          8%  { opacity: 1; transform: scale(1.3); }
          18% { opacity: 0; }
          42% { opacity: 1; transform: scale(1.4); }
          50% { opacity: 0; }
          78% { opacity: 1; transform: scale(1.2); }
          85% { opacity: 0; }
        }
        @keyframes grid-drift {
          0%   { transform: translateX(0) translateY(0); }
          100% { transform: translateX(40px) translateY(20px); }
        }
        @keyframes service-card-grow {
          0% {
            transform:
              translate3d(var(--origin-x), var(--origin-y), 0)
              scale(var(--origin-scale-x), var(--origin-scale-y));
            border-radius: 1rem;
            opacity: 0.98;
          }
          72% {
            transform:
              translate3d(0, 0, 0)
              scale(1.02, 1.015);
          }
          100% {
            transform:
              translate3d(0, 0, 0)
              scale(1);
            border-radius: 1.5rem;
            opacity: 1;
          }
        }
        @keyframes service-card-return {
          0% {
            width: var(--panel-width);
            height: var(--panel-height);
            transform:
              translate3d(0, 0, 0)
              scale(1);
            border-radius: 1.5rem;
            opacity: 1;
          }
          28% {
            width: var(--panel-width);
            height: var(--panel-height);
            transform:
              translate3d(0, 0, 0)
              scale(1.02, 1.015);
            opacity: 1;
          }
          100% {
            width: var(--origin-width);
            height: var(--origin-height);
            transform:
              translate3d(var(--origin-x), var(--origin-y), 0)
              scale(1);
            border-radius: 1rem;
            opacity: 1;
          }
        }
        @keyframes service-card-flip {
          0% { transform: rotateY(0deg); }
          100% { transform: rotateY(180deg); }
        }
        @keyframes service-card-unflip {
          0% { transform: rotateY(180deg); }
          100% { transform: rotateY(0deg); }
        }
      `}</style>

      {/* Moving dot grid */}
      <div className="absolute inset-0 opacity-20" style={{
        backgroundImage: "radial-gradient(circle, #2557C7 1.5px, transparent 1.5px)",
        backgroundSize: "36px 36px",
        animation: "grid-drift 22s linear infinite alternate"
      }} />

      {/* Hidden SVG defs for glow filter */}
      <svg width="0" height="0" style={{position:"absolute"}}>
        <defs>
          <filter id="arc-glow" x="-20%" y="-100%" width="140%" height="300%">
            <feGaussianBlur stdDeviation="4" result="glow"/>
            <feMerge><feMergeNode in="glow"/><feMergeNode in="glow"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>
      </svg>

      {/* Full-width arc layer — all arcs live in one SVG spanning the section */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none" viewBox="0 0 1000 500" fill="none">

        {/* Arc 1 — CYAN — top area */}
        <path d="M0,110 L60,95 L110,125 L180,90 L250,118 L310,85 L380,115 L450,88 L520,112 L590,82 L660,108 L730,78 L800,105 L870,80 L940,102 L1000,95"
          stroke="#00e5ff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          strokeDasharray="1200" style={{animation: "arc-travel-1 9s ease-in-out infinite", filter: "url(#arc-glow)"}}/>
        <path d="M0,110 L60,95 L110,125 L180,90 L250,118 L310,85 L380,115 L450,88 L520,112 L590,82 L660,108 L730,78 L800,105 L870,80 L940,102 L1000,95"
          stroke="white" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"
          strokeDasharray="1200" style={{animation: "arc-travel-1 9s ease-in-out infinite"}}/>

        {/* Arc 2 — PURPLE — upper-mid */}
        <path d="M0,200 L55,182 L120,215 L195,185 L265,220 L340,188 L405,218 L478,182 L548,210 L615,178 L685,208 L758,175 L825,205 L895,178 L955,200 L1000,190"
          stroke="#c026d3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          strokeDasharray="1200" style={{animation: "arc-travel-2 11s ease-in-out infinite 2s", filter: "url(#arc-glow)"}}/>
        <path d="M0,200 L55,182 L120,215 L195,185 L265,220 L340,188 L405,218 L478,182 L548,210 L615,178 L685,208 L758,175 L825,205 L895,178 L955,200 L1000,190"
          stroke="white" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round"
          strokeDasharray="1200" style={{animation: "arc-travel-2 11s ease-in-out infinite 2s"}}/>

        {/* Arc 3 — GOLD — middle */}
        <path d="M0,290 L70,272 L135,305 L205,268 L278,300 L348,265 L418,298 L492,262 L562,295 L632,260 L702,292 L775,258 L842,288 L912,260 L968,285 L1000,278"
          stroke="#f59e0b" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
          strokeDasharray="1200" style={{animation: "arc-travel-3 10s ease-in-out infinite 4.5s", filter: "url(#arc-glow)"}}/>
        <path d="M0,290 L70,272 L135,305 L205,268 L278,300 L348,265 L418,298 L492,262 L562,295 L632,260 L702,292 L775,258 L842,288 L912,260 L968,285 L1000,278"
          stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"
          strokeDasharray="1200" style={{animation: "arc-travel-3 10s ease-in-out infinite 4.5s"}}/>

        {/* Arc 4 — GREEN — lower */}
        <path d="M0,375 L65,355 L130,385 L200,350 L272,382 L345,348 L415,378 L488,345 L558,375 L628,342 L698,372 L770,340 L838,368 L908,338 L966,362 L1000,355"
          stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          strokeDasharray="1200" style={{animation: "arc-travel-4 13s ease-in-out infinite 1s", filter: "url(#arc-glow)"}}/>
        <path d="M0,375 L65,355 L130,385 L200,350 L272,382 L345,348 L415,378 L488,345 L558,375 L628,342 L698,372 L770,340 L838,368 L908,338 L966,362 L1000,355"
          stroke="white" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round"
          strokeDasharray="1200" style={{animation: "arc-travel-4 13s ease-in-out infinite 1s"}}/>

        {/* Arc 5 — BLUE — slightly lower than arc 1 */}
        <path d="M0,155 L80,138 L148,168 L220,132 L292,162 L365,128 L435,158 L508,124 L578,155 L648,122 L718,152 L790,120 L858,148 L928,118 L978,142 L1000,135"
          stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          strokeDasharray="1200" style={{animation: "arc-travel-5 8s ease-in-out infinite 6s", filter: "url(#arc-glow)"}}/>
        <path d="M0,155 L80,138 L148,168 L220,132 L292,162 L365,128 L435,158 L508,124 L578,155 L648,122 L718,152 L790,120 L858,148 L928,118 L978,142 L1000,135"
          stroke="white" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round"
          strokeDasharray="1200" style={{animation: "arc-travel-5 8s ease-in-out infinite 6s"}}/>
      </svg>

      <div className="container mx-auto px-6 relative z-10">
        <div ref={headerRef} className="reveal mb-14">
          <div className="w-10 h-1 bg-[#2557C7] mb-5" />
          <h2 className="text-4xl font-black text-slate-900 mb-3">Our Services</h2>
          <p className="text-slate-600 text-lg max-w-xl">
            Commercial electrical services for businesses across Greater New Orleans, backed by dependable residential support when customers need it.
          </p>
        </div>

        {/* 3 big image cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {services.map((service) => (
            <ServiceCard
              key={service.title}
              service={service}
              isHidden={selectedService?.service.title === service.title}
              onSelect={(origin) => openService(service, origin)}
            />
          ))}
        </div>
      </div>

      {selectedService?.service.expanded && (
        <ServiceDetailOverlay
          service={selectedService.service}
          origin={selectedService.origin}
          isClosing={isClosing}
          onClose={closeService}
        />
      )}
    </section>
  );
}

function ServiceCard({
  service,
  isHidden,
  onSelect,
}: {
  service: Service;
  isHidden: boolean;
  onSelect: (origin: DOMRect) => void;
}) {
  const isVueCard = service.title === "Energy Efficiency and Safety";
  const isCommercialCard = service.title === "Commercial Services";
  const isResidentialCard = service.title === "Residential Electrical";
  const haloClass = isCommercialCard
    ? "hover:shadow-[0_0_55px_rgba(37,87,199,0.45)] hover:ring-blue-400/50"
    : isVueCard
      ? "hover:shadow-[0_0_55px_rgba(34,197,94,0.42)] hover:ring-green-400/45"
      : "hover:shadow-[0_0_55px_rgba(245,158,11,0.42)] hover:ring-amber-400/45";
  const serviceAccent = isCommercialCard ? "#2557C7" : isResidentialCard ? "#f59e0b" : "#22c55e";
  const className = `group relative min-h-[420px] overflow-hidden rounded-2xl bg-slate-900 shadow-lg ring-1 ring-white/10 transition-[transform,box-shadow,ring-color,ring-width] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-1.5 hover:ring-2 ${haloClass} ${isVueCard ? "cursor-pointer" : ""}`;

  const content = (
    <>
      <img
        src={service.image}
        alt={service.title}
        className="absolute inset-0 h-full w-full object-cover opacity-40 transition-all duration-700 group-hover:scale-105 group-hover:opacity-55"
      />
      <div className="absolute inset-0 bg-linear-to-t from-slate-950 via-slate-900/60 to-transparent" />

      <div className="relative z-10 flex min-h-[420px] flex-col p-8">
        <div className="mt-auto">
          <h3 className="mb-3 text-xl font-black leading-tight text-white">
            {service.title}
          </h3>
          <p className="mb-5 text-sm leading-relaxed text-slate-300">
            {service.description}
          </p>

          {isVueCard ? (
            <span className="inline-flex items-center gap-2 text-green-400 text-sm font-bold group-hover:text-green-300 transition-colors">
              Learn about VUE Smart Systems →
            </span>
          ) : (
            <span
              className={`inline-flex items-center gap-2 text-sm font-bold transition-colors ${
                isCommercialCard ? "text-blue-400 hover:text-blue-300" : "text-amber-500 hover:text-amber-400"
              }`}
            >
              View Details →
            </span>
          )}
        </div>
      </div>
    </>
  );

  if (isVueCard) {
    return (
      <div className={className}>
        <Link to="/vue-smart-home" className="block">
          {content}
        </Link>
      </div>
    );
  }

  return (
    <div
      className={`${className} cursor-pointer ${isHidden ? "pointer-events-none invisible" : "visible"}`}
      onClick={(event) => onSelect(event.currentTarget.getBoundingClientRect())}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(event.currentTarget.getBoundingClientRect());
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`View details for ${service.title}`}
    >
      {content}
    </div>
  );
}

function ServiceDetailOverlay({
  service,
  origin,
  isClosing,
  onClose,
}: {
  service: Service;
  origin: CardOrigin;
  isClosing: boolean;
  onClose: () => void;
}) {
  if (!service.expanded) return null;

  const isCommercialCard = service.title === "Commercial Services";
  const serviceAccent = isCommercialCard ? "#2557C7" : "#f59e0b";
  const detailImage = service.expandedImage ?? service.image;
  const viewportWidth = typeof window === "undefined" ? 1024 : window.innerWidth;
  const viewportHeight = typeof window === "undefined" ? 768 : window.innerHeight;
  const panelWidth = Math.min(viewportWidth - 56, 1120);
  const panelHeight = Math.min(viewportHeight - 104, 760);
  const originCenterX = origin.left + origin.width / 2;
  const originCenterY = origin.top + origin.height / 2;
  const finalCenterX = viewportWidth / 2;
  const finalCenterY = viewportHeight / 2;
  const morphStyle = {
    "--origin-x": `${originCenterX - finalCenterX}px`,
    "--origin-y": `${originCenterY - finalCenterY}px`,
    "--origin-scale-x": `${origin.width / panelWidth}`,
    "--origin-scale-y": `${origin.height / panelHeight}`,
    "--origin-width": `${origin.width}px`,
    "--origin-height": `${origin.height}px`,
    "--panel-width": `${panelWidth}px`,
    "--panel-height": `${panelHeight}px`,
    "--service-accent": serviceAccent,
    animationName: isClosing ? "service-card-return" : "service-card-grow",
    animationDuration: "680ms",
    animationTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
    animationFillMode: "both",
    transformOrigin: "center",
    width: panelWidth,
    height: panelHeight,
  } as CSSProperties;

  return (
    <div
      className="fixed inset-0 z-70 flex items-center justify-center px-4 py-8"
      style={{ perspective: "1400px" }}
      onClick={onClose}
    >
      <div
        className="relative overflow-visible rounded-3xl text-white"
        style={morphStyle}
        onClick={(event) => event.stopPropagation()}
      >
        <div
          className="relative h-full w-full"
          style={{
            animationName: isClosing ? "service-card-unflip" : "service-card-flip",
            animationDuration: "680ms",
            animationTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
            animationFillMode: "both",
            transformStyle: "preserve-3d",
          }}
        >
          <div
            className="absolute inset-0 overflow-hidden rounded-3xl bg-slate-950 shadow-2xl ring-1 ring-white/15"
            style={{
              backfaceVisibility: "hidden",
              pointerEvents: "none",
              transformOrigin: "center",
            }}
          >
            <img
              src={service.image}
              alt=""
              className="absolute inset-0 h-full w-full object-cover opacity-40"
              aria-hidden="true"
            />
            <div className="absolute inset-0 bg-linear-to-t from-slate-950 via-slate-900/60 to-transparent" />
            <div className="relative z-10 flex h-full flex-col p-8">
              <div className="mt-auto">
                <h3 className="mb-3 text-xl font-black leading-tight text-white">
                  {service.title}
                </h3>
                <p className="mb-5 text-sm leading-relaxed text-slate-300">
                  {service.description}
                </p>
                <span
                  className="inline-flex items-center gap-2 text-sm font-bold"
                  style={{ color: serviceAccent }}
                >
                  View Details →
                </span>
              </div>
            </div>
          </div>

          <div
            className="absolute inset-0 overflow-hidden rounded-3xl bg-slate-950 shadow-2xl ring-1 ring-white/15"
            style={{
              backfaceVisibility: "hidden",
              pointerEvents: "auto",
              transform: "rotateY(180deg)",
              transformOrigin: "center",
            }}
          >
            <img
              src={detailImage}
              alt=""
              className="absolute inset-0 h-full w-full object-cover opacity-50"
              aria-hidden="true"
            />
            <div className="absolute inset-0 bg-linear-to-r from-slate-950/95 via-slate-950/75 to-slate-950/35" />
            <div
              className="absolute inset-y-0 left-0 w-1.5"
              style={{ backgroundColor: serviceAccent }}
            />

            <div className="relative z-10 flex h-full flex-col p-6 sm:p-8 lg:p-10">
              <button
                type="button"
                onClick={onClose}
                className="absolute right-5 top-5 rounded-full bg-white/10 p-2 text-white backdrop-blur transition-colors hover:bg-white/20"
                aria-label={`Close ${service.title} details`}
              >
                <X className="h-5 w-5" />
              </button>

              <div className="w-full pr-14">
                <p className="mb-3 text-xs font-black uppercase tracking-[0.18em]" style={{ color: serviceAccent }}>
                  Service Details
                </p>
                <h3 className="mb-4 text-3xl font-black leading-tight sm:text-4xl">
                  {service.title}
                </h3>
                <p className="text-base leading-relaxed text-slate-300">
                  {service.expanded.intro}
                </p>
              </div>

              <div className="mt-7 grid gap-4 lg:grid-cols-3">
                {service.expanded.sections.map((section) => (
                  <div key={section.title} className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur-sm">
                    <h4 className="mb-3 text-xs font-black uppercase tracking-[0.14em] text-white">
                      {section.title}
                    </h4>
                    <ul className="space-y-2">
                      {section.items.map((item) => (
                        <li key={item} className="flex gap-2.5 text-xs font-medium leading-snug text-slate-300">
                          <span
                            className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full"
                            style={{ backgroundColor: serviceAccent }}
                          />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="mt-auto flex flex-wrap gap-3 pt-5">
                <a
                  href="#contact"
                  onClick={onClose}
                  className="rounded-lg px-6 py-3 font-bold text-white transition-opacity hover:opacity-90"
                  style={{ backgroundColor: serviceAccent }}
                >
                  Request Estimate
                </a>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg border border-white/15 px-6 py-3 font-bold text-white transition-colors hover:bg-white/10"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
